/**
 * Pharmacy Page
 * 
 * Dashboard view of all pharmacies with filtering and program relationships
 */

import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { pharmacyAPI, apiHelpers } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import useDraftStore from '../stores/draftStore'
import PharmacyEditModal from '../components/PharmacyEditModal'
import ImportPharmaciesModal from '../components/ImportPharmaciesModal'
import api from '../services/api'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Badge
} from '../components/ui'

export default function PharmacyPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [pharmacies, setPharmacies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState(null)
  const [pagination, setPagination] = useState({})

  // Get auth state to check clinic selection
  const { user, currentClinicId } = useAuthStore()
  const { addChange, getChangesByType } = useDraftStore()

  // Roles that can edit configs (excludes viewer and data_entry)
  const canEditConfigs = user && ['super_admin', 'clinic_admin', 'pharmacy_manager'].includes(user.role)

  // Helper function to merge drafts with database pharmacies
  const mergePharmaciesWithDrafts = (dbPharmacies) => {
    const draftChanges = getChangesByType('pharmacy')
    
    console.log('[MERGE] Database pharmacies:', dbPharmacies.length)
    console.log('[MERGE] Draft changes:', draftChanges.length)
    
    // Start with database pharmacies
    let mergedPharmacies = [...dbPharmacies]
    
    // Apply draft changes
    draftChanges.forEach(change => {
      if (change.operation === 'create') {
        // Add new pharmacies from drafts with a draft indicator
        mergedPharmacies.push({
          ...change.new_data,
          _isDraft: true,
          _draftChangeId: change.id
        })
      } else if (change.operation === 'update') {
        // Update existing pharmacies or drafts
        const index = mergedPharmacies.findIndex(p => 
          p._id === change.resource_id || p.pharmacy_ncpdpid === change.resource_id
        )
        if (index !== -1) {
          mergedPharmacies[index] = {
            ...mergedPharmacies[index],
            ...change.new_data,
            _isDraft: true,
            _draftChangeId: change.id
          }
        }
      } else if (change.operation === 'delete') {
        // Remove deleted pharmacies
        mergedPharmacies = mergedPharmacies.filter(p => 
          p._id !== change.resource_id && p.pharmacy_ncpdpid !== change.resource_id
        )
      }
    })
    
    console.log('[MERGE] Merged pharmacies:', mergedPharmacies.length)
    
    return mergedPharmacies
  }

  // Search and filter state
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [stateFilter, setStateFilter] = useState(searchParams.get('state') || 'all')
  const [gatewayFilter, setGatewayFilter] = useState(searchParams.get('gateway') || 'all')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all')
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'pharmacy_name')
  const [sortOrder, setSortOrder] = useState(searchParams.get('sort_order') || 'asc')
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1)
  
  // Modal state
  const [isPharmacyModalOpen, setIsPharmacyModalOpen] = useState(false)
  const [editingPharmacy, setEditingPharmacy] = useState(null)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  
  // Modal state for pharmacy details
  const [selectedPharmacy, setSelectedPharmacy] = useState(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [isProgramsModalOpen, setIsProgramsModalOpen] = useState(false)
  const [pharmacyPrograms, setPharmacyPrograms] = useState([])
  const [loadingPrograms, setLoadingPrograms] = useState(false)

  // Check if user has access - super admins can access even without clinic selection
  const hasClinicAccess = user?.role === 'super_admin' || (currentClinicId && user?.clinicIds?.includes(currentClinicId))

  // Fetch pharmacies
  const fetchPharmacies = async () => {
    if (!hasClinicAccess) {
      setError('Please select a clinic to view pharmacy data')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const params = {
        search: search || undefined,
        state: stateFilter !== 'all' ? stateFilter : undefined,
        gateway: gatewayFilter !== 'all' ? gatewayFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: currentPage,
        limit: 20,
        include_disabled: 'false'
      }

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key])

      const response = await pharmacyAPI.list(params)
      
      if (response.data.success) {
        const pharmaciesList = response.data.pharmacies || response.data.data || []
        
        // Merge with draft changes
        const mergedPharmacies = mergePharmaciesWithDrafts(pharmaciesList)
        setPharmacies(mergedPharmacies)
        setPagination(response.data.pagination)
        
        console.log('[PHARMACY PAGE] Set pharmacies (with drafts):', mergedPharmacies.length)
      } else {
        throw new Error('Failed to fetch pharmacies')
      }
    } catch (err) {
      console.error('Failed to fetch pharmacies:', err)
      setError(apiHelpers.handleError(err).message)
    } finally {
      setLoading(false)
    }
  }

  // Fetch statistics
  const fetchStats = async () => {
    if (!hasClinicAccess) return

    try {
      const response = await pharmacyAPI.getStats()
      if (response.data.success) {
        setStats(response.data.data)
      }
    } catch (err) {
      console.error('Failed to fetch pharmacy stats:', err)
    }
  }

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (stateFilter !== 'all') params.set('state', stateFilter)
    if (gatewayFilter !== 'all') params.set('gateway', gatewayFilter)
    if (statusFilter !== 'all') params.set('status', statusFilter)
    if (sortBy !== 'pharmacy_name') params.set('sort_by', sortBy)
    if (sortOrder !== 'asc') params.set('sort_order', sortOrder)
    if (currentPage > 1) params.set('page', currentPage.toString())

    setSearchParams(params)
  }, [search, stateFilter, gatewayFilter, statusFilter, sortBy, sortOrder, currentPage, setSearchParams])

  // Fetch data when filters change
  useEffect(() => {
    fetchPharmacies()
  }, [search, stateFilter, gatewayFilter, statusFilter, sortBy, sortOrder, currentPage, currentClinicId])

  // Fetch stats on mount
  useEffect(() => {
    fetchStats()
  }, [currentClinicId])

  // Handle search input with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearch(value)
    setCurrentPage(1) // Reset to first page when searching
  }

  // Get status badge variant
  const getStatusVariant = (pharmacy) => {
    if (!pharmacy.pharmacy_config?.enable_pharmacy) return 'secondary'
    
    const config = pharmacy.pharmacy_config || {}
    const utilization = config.weekly_order_capacity ? 
      Math.round((config.current_week_orders / config.weekly_order_capacity) * 100) : 0
    
    if (utilization >= 95) return 'destructive'
    if (utilization >= 80) return 'outline'
    return 'default'
  }

  // Get status text
  const getStatusText = (pharmacy) => {
    if (!pharmacy.pharmacy_config?.enable_pharmacy) return 'Disabled'
    
    const config = pharmacy.pharmacy_config || {}
    const utilization = config.weekly_order_capacity ? 
      Math.round((config.current_week_orders / config.weekly_order_capacity) * 100) : 0
    
    if (utilization >= 95) return 'At Capacity'
    if (utilization >= 80) return 'Near Capacity'
    return 'Available'
  }

  // Handle pharmacy detail view
  const handleViewDetails = (pharmacy) => {
    setSelectedPharmacy(pharmacy)
    setIsDetailModalOpen(true)
  }

  // Load programs for a pharmacy
  const loadPharmacyPrograms = async (pharmacyId) => {
    try {
      setLoadingPrograms(true)
      const response = await api.get(`/pharmacy/${pharmacyId}/programs`)
      setPharmacyPrograms(response.data.data || [])
      setIsProgramsModalOpen(true)
    } catch (error) {
      console.error('Error loading pharmacy programs:', error)
      alert('Failed to load programs for this pharmacy')
    } finally {
      setLoadingPrograms(false)
    }
  }

  // Toggle pharmacy active status
  const handleTogglePharmacyStatus = (pharmacy) => {
    const currentStatus = pharmacy.pharmacy_config?.enable_pharmacy ?? true
    const newStatus = !currentStatus
    const statusText = newStatus ? 'enable' : 'disable'
    
    if (!confirm(`Are you sure you want to ${statusText} "${pharmacy.pharmacy_name}"?`)) {
      return
    }

    try {
      const updatedPharmacy = {
        ...pharmacy,
        pharmacy_config: {
          ...(pharmacy.pharmacy_config || {}),
          enable_pharmacy: newStatus
        }
      }

      // Add change to draft store
      const action = newStatus ? 'Enable' : 'Disable'
      const details = []
      if (pharmacy.pharmacy_ncpdpid) details.push(`NCPDP: ${pharmacy.pharmacy_ncpdpid}`)
      if (pharmacy.pharmacy_gateway) details.push(pharmacy.pharmacy_gateway)
      if (pharmacy.states?.length) details.push(`${pharmacy.states.length} states`)
      
      addChange({
        resource_type: 'pharmacy',
        resource_id: pharmacy._id || pharmacy.pharmacy_ncpdpid,
        operation: 'update',
        original_data: pharmacy,
        new_data: updatedPharmacy,
        description: `${action}: ${pharmacy.pharmacy_name} (${details.join(', ')})`
      })

      console.log(`[PHARMACY] ${newStatus ? 'Enabled' : 'Disabled'} pharmacy in drafts`)
      
      // Close detail modal if open
      setIsDetailModalOpen(false)
      
      // Show success message
      alert(`Pharmacy "${pharmacy.pharmacy_name}" will be ${newStatus ? 'enabled' : 'disabled'} when you publish your changes.`)
    } catch (error) {
      console.error('Error toggling pharmacy status:', error)
      alert('Failed to toggle pharmacy status')
    }
  }

  if (!hasClinicAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Clinic Selection Required</CardTitle>
            <CardDescription>Please select a clinic from the header dropdown to view pharmacies.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Pharmacies</h1>
              <p className="text-muted-foreground mt-2">
                Manage pharmacy partnerships and program configurations
              </p>
            </div>
            {canEditConfigs && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsImportModalOpen(true)}
                >
                  Import Pharmacies
                </Button>
                <Button
                  onClick={() => {
                    setEditingPharmacy(null)
                    setIsPharmacyModalOpen(true)
                  }}
                >
                  Add Pharmacy
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Active</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.overview?.total_active || 0}</div>
                <p className="text-xs text-muted-foreground">
                  +{stats.overview?.total_disabled || 0} disabled
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Capacity Available</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.capacity?.available || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.capacity?.near_capacity || 0} near capacity
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">5Axis Gateway</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.by_gateway?.['5Axis'] || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.by_gateway?.['SelfShip'] || 0} self-ship
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Weekly Utilization</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.capacity?.total_capacity ? 
                    Math.round((stats.capacity.total_current / stats.capacity.total_capacity) * 100) : 0}%
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats.capacity?.total_current || 0} / {stats.capacity?.total_capacity || 0} orders
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="container mx-auto px-4 pb-6">
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  placeholder="Search pharmacies..."
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="state-filter">State</Label>
                <Select value={stateFilter} onValueChange={setStateFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All states" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    {stats?.by_state?.slice(0, 10).map(state => (
                      <SelectItem key={state._id} value={state._id}>
                        {state._id} ({state.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="gateway-filter">Gateway</Label>
                <Select value={gatewayFilter} onValueChange={setGatewayFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All gateways" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Gateways</SelectItem>
                    <SelectItem value="5Axis">5Axis</SelectItem>
                    <SelectItem value="SelfShip">SelfShip</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status-filter">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="near_capacity">Near Capacity</SelectItem>
                    <SelectItem value="at_capacity">At Capacity</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sort">Sort By</Label>
                <Select value={`${sortBy}:${sortOrder}`} onValueChange={(value) => {
                  const [field, order] = value.split(':')
                  setSortBy(field)
                  setSortOrder(order)
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pharmacy_name:asc">Name (A-Z)</SelectItem>
                    <SelectItem value="pharmacy_name:desc">Name (Z-A)</SelectItem>
                    <SelectItem value="created_at:desc">Newest First</SelectItem>
                    <SelectItem value="created_at:asc">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-pulse text-muted-foreground">Loading pharmacies...</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {error && (
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8">
              <div className="text-center text-destructive">
                <p>Error loading pharmacies: {error}</p>
                <Button 
                  variant="outline" 
                  onClick={fetchPharmacies}
                  className="mt-4"
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pharmacies List */}
      {!loading && !error && (
        <div className="container mx-auto px-4 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pharmacies.map((pharmacy) => (
              <Card 
                key={pharmacy._id || pharmacy.pharmacy_ncpdpid || pharmacy._draftChangeId} 
                className={`hover:shadow-md transition-shadow ${pharmacy._isDraft ? 'border-amber-500 border-2' : ''}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{pharmacy.pharmacy_name}</CardTitle>
                        {pharmacy._isDraft && (
                          <Badge className="bg-amber-500 text-white text-xs">DRAFT</Badge>
                        )}
                      </div>
                      <CardDescription className="mt-1">
                        {pharmacy.address?.city}, {pharmacy.address?.state} • {pharmacy.pharmacy_gateway}
                      </CardDescription>
                    </div>
                    <Badge variant={getStatusVariant(pharmacy)}>
                      {getStatusText(pharmacy)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">NCPDP ID:</span>
                      <span className="font-mono">{pharmacy.pharmacy_ncpdpid}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">States:</span>
                      <span>{pharmacy.states?.length || 0} states</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Weekly Orders:</span>
                      <span>
                        {pharmacy.pharmacy_config?.current_week_orders || 0} / {pharmacy.pharmacy_config?.weekly_order_capacity || 0}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/pharmacy/${pharmacy._id}`)}
                      className="flex-1"
                    >
                      View Details
                    </Button>
                    {canEditConfigs && (
                      <Button variant="outline" size="sm" disabled>
                        Edit
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button 
                variant="outline" 
                disabled={!pagination.has_prev}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </Button>
              <span className="px-4 py-2 text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.pages}
              </span>
              <Button 
                variant="outline" 
                disabled={!pagination.has_next}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}

          {/* Empty State */}
          {pharmacies.length === 0 && !loading && (
            <Card>
              <CardContent className="p-8">
                <div className="text-center">
                  <p className="text-muted-foreground mb-4">No pharmacies found matching your criteria.</p>
                  <Button variant="outline" onClick={() => {
                    setSearch('')
                    setStateFilter('all')
                    setGatewayFilter('all')
                    setStatusFilter('all')
                    setCurrentPage(1)
                  }}>
                    Clear Filters
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Pharmacy Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedPharmacy?.pharmacy_name}</DialogTitle>
            <DialogDescription>
              Pharmacy details and program configurations
            </DialogDescription>
          </DialogHeader>
          
          {selectedPharmacy && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Contact Information</h4>
                  <div className="space-y-1 text-sm">
                    <p>{selectedPharmacy.address?.address}</p>
                    {selectedPharmacy.address?.line2 && <p>{selectedPharmacy.address.line2}</p>}
                    <p>{selectedPharmacy.address?.city}, {selectedPharmacy.address?.state} {selectedPharmacy.address?.zipcode}</p>
                    <p className="font-mono">{selectedPharmacy.phone_number}</p>
                    <p className="text-muted-foreground">{selectedPharmacy.contact_emails?.[0]}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Configuration</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Gateway:</span> {selectedPharmacy.pharmacy_gateway}</p>
                    <p><span className="text-muted-foreground">NCPDP ID:</span> {selectedPharmacy.pharmacy_ncpdpid}</p>
                    <p><span className="text-muted-foreground">DoseSpot ID:</span> {selectedPharmacy.dosespot_pharmacy_id}</p>
                    <p><span className="text-muted-foreground">States:</span> {selectedPharmacy.states?.sort().join(', ')}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={() => loadPharmacyPrograms(selectedPharmacy._id)}
                  disabled={loadingPrograms}
                >
                  {loadingPrograms ? 'Loading...' : 'View Programs'}
                </Button>
{canEditConfigs && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingPharmacy(selectedPharmacy)
                        setIsPharmacyModalOpen(true)
                        setIsDetailModalOpen(false)
                      }}
                    >
                      Edit Pharmacy
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleTogglePharmacyStatus(selectedPharmacy)}
                    >
                      {selectedPharmacy?.pharmacy_config?.enable_pharmacy ?? true ? 'Disable' : 'Enable'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Pharmacy Add/Edit Modal */}
      <PharmacyEditModal
        isOpen={isPharmacyModalOpen}
        onClose={() => {
          setIsPharmacyModalOpen(false)
          setEditingPharmacy(null)
        }}
        pharmacy={editingPharmacy}
        onSave={() => {
          // Reload pharmacies list after save
          fetchPharmacies()
        }}
      />
      
      {/* Import Pharmacies Modal */}
      <ImportPharmaciesModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        currentClinicId={currentClinicId}
        onImport={() => {
          // Reload pharmacies list after import
          fetchPharmacies()
        }}
      />

      {/* Pharmacy Programs Modal */}
      <Dialog open={isProgramsModalOpen} onOpenChange={setIsProgramsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Drug Programs at {selectedPharmacy?.pharmacy_name}</DialogTitle>
            <DialogDescription>
              Programs available through this pharmacy
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {pharmacyPrograms.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No drug programs configured for this pharmacy
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Drug Name</th>
                      <th className="text-left p-3 font-medium">Type</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pharmacyPrograms.map((program) => (
                      <tr key={program._id} className="border-t hover:bg-muted/30">
                        <td className="p-3 font-medium">{program.drug_name}</td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {program.program_type || 'N/A'}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            program.can_create_order ? 
                            'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100' : 
                            'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100'
                          }`}>
                            {program.can_create_order ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-3">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              window.location.href = `/drug-programs/${program._id}`
                            }}
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setIsProgramsModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
